import { Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EnrichedAttributeRow } from '~/components/common/enriched-attribute-row';
import { Badge } from '~/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { Skeleton } from '~/components/ui/skeleton';
import { applyCurrentValues } from '~/features/catalog-operations/mappers/catalog-operations.mapper';
import type { ProductEnrichmentRun } from '~/features/catalog-operations/services/catalog-operations.service';
import { useCatalogEnrichmentHistory } from '~/features/product-catalog/hooks/use-catalog-enrichment-history';
import { formatDateTime } from '~/utils/date';

interface CatalogEnrichmentHistoryProps {
  /** Catalog external id of the open product — the history lookup key. */
  externalId: string;
  /** Integrations connector code of the catalog (e.g. `GOOGLE_MERCHANT`). */
  connectorCode: string;
  /** The catalog row (merged with its detail) — supplies the "Current" side. */
  product: Record<string, unknown>;
  enabled: boolean;
}

/** String-valued catalog fields keyed by name, for the comparison's "Current"
 *  side. Non-strings (arrays, scores) have no single-text form and are skipped. */
function toCurrentFields(product: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(product)) {
    if (typeof value === 'string' && value) out[key] = value;
  }
  return out;
}

/** Stable run key — one workflow can hold both a SAMPLE and a FULL row for a product. */
function runKey(run: ProductEnrichmentRun): string {
  return `${run.workflowId ?? ''}:${run.phase ?? ''}`;
}

/** Run label for the picker: when it ran, and whether it was a sample or full pass. */
function runLabel(run: ProductEnrichmentRun, phaseLabel: (phase: string) => string): string {
  const when = run.enrichedAt ? formatDateTime(run.enrichedAt) : (run.workflowId ?? '');
  return run.phase ? `${when} · ${phaseLabel(run.phase)}` : when;
}

/**
 * Enrichment tab of the Product Catalog detail panel: every run that enriched
 * this product (newest first), a picker to switch runs, and the selected run's
 * before/after attribute comparison. Shows the single run directly when there
 * is only one.
 */
export function CatalogEnrichmentHistory({
  externalId,
  connectorCode,
  product,
  enabled,
}: CatalogEnrichmentHistoryProps) {
  const { t } = useTranslation('explorer');
  const historyQuery = useCatalogEnrichmentHistory(externalId, connectorCode, enabled);
  const runs = useMemo(() => historyQuery.data ?? [], [historyQuery.data]);

  // Selection is by run key so a refetch can't shift it onto another run;
  // falls back to the newest run (also when switching products).
  const [pickedRunKey, setPickedRunKey] = useState<string | null>(null);
  const activeRun = runs.find((r) => runKey(r) === pickedRunKey) ?? runs[0];

  const currentFields = useMemo(() => toCurrentFields(product), [product]);
  const optimizations = useMemo(
    () => (activeRun ? applyCurrentValues(activeRun.optimizations ?? [], currentFields) : []),
    [activeRun, currentFields],
  );

  const phaseLabel = (phase: string) =>
    phase === 'SAMPLE' ? t('products.enrichment.phaseSample') : t('products.enrichment.phaseFull');

  if (historyQuery.isLoading) {
    return (
      <div className="space-y-3 px-5 py-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (historyQuery.isError) {
    return (
      <p className="text-muted-foreground px-5 py-8 text-center text-sm">
        {t('products.enrichment.loadError')}
      </p>
    );
  }

  if (!activeRun) {
    return (
      <div className="flex flex-col items-center gap-2 px-5 py-10 text-center">
        <Sparkles aria-hidden="true" className="text-muted-foreground h-6 w-6" />
        <p className="text-foreground text-sm font-medium">{t('products.enrichment.emptyTitle')}</p>
        <p className="text-muted-foreground text-sm">{t('products.enrichment.emptyBody')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {runs.length > 1 ? (
          <Select value={runKey(activeRun)} onValueChange={setPickedRunKey}>
            <SelectTrigger
              aria-label={t('products.enrichment.runLabel')}
              className="h-9 w-auto min-w-[240px]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {runs.map((run, i) => (
                <SelectItem key={runKey(run)} value={runKey(run)} className="cursor-pointer">
                  {runLabel(run, phaseLabel)}
                  {i === 0 && ` · ${t('products.enrichment.latest')}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <p className="text-foreground text-sm font-medium">{runLabel(activeRun, phaseLabel)}</p>
        )}
        <span className="text-muted-foreground text-xs">
          {t('products.enrichment.runCount', { count: runs.length })}
        </span>
      </div>

      {activeRun.enrichmentScore && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t('products.enrichment.score')}</span>
          <Badge variant="outline" className="tabular-nums">
            {activeRun.enrichmentScore.before}
          </Badge>
          <span aria-hidden="true" className="text-muted-foreground">
            →
          </span>
          <Badge className="tabular-nums">{activeRun.enrichmentScore.after}</Badge>
        </div>
      )}

      {optimizations.length > 0 ? (
        <div className="bg-background divide-y overflow-hidden rounded-2xl border shadow-sm">
          {optimizations.map((opt) => (
            <EnrichedAttributeRow key={opt.field} opt={opt} />
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground py-6 text-center text-sm">
          {t('products.enrichment.noFields')}
        </p>
      )}
    </div>
  );
}
